import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useUser } from '../context/UserContext';
import { CircularProgress, Box } from "@mui/material";

const ProtectedRoute = ({ children }) => {
  const {
    user,
    loadingUser,
    hasUsername,
    userRepsBioguideID,
    userSenOneBioguideID,
    userSenTwoBioguideID,
    addressSkipped,
    userDistrict,
    refreshFirebaseUser,
  } = useUser();

  const verificationRefreshAttempted = useRef(false);
  const [checkingVerification, setCheckingVerification] = useState(false);

  useEffect(() => {
    verificationRefreshAttempted.current = false;
  }, [user?.uid]);

  useEffect(() => {
    if (loadingUser || !user || user.emailVerified) return;
    if (verificationRefreshAttempted.current) return;

    verificationRefreshAttempted.current = true;
    let cancelled = false;

    (async () => {
      setCheckingVerification(true);
      try {
        await refreshFirebaseUser();
      } finally {
        if (!cancelled) setCheckingVerification(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadingUser, user, refreshFirebaseUser]);

  if (loadingUser || checkingVerification) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }

  if (user && !user.emailVerified) {
    if (!verificationRefreshAttempted.current) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    return <Navigate to="/verify-email" replace />;
  }

  if (!hasUsername) {
    const hasRepOnFile = Boolean(
      userRepsBioguideID || userSenOneBioguideID || userSenTwoBioguideID
    );
    if (hasRepOnFile) {
      return <Navigate to="/username-selection" replace />;
    }
    return <Navigate to="/setup-representative" replace />;
  }

  // A district on file means the address step is done, even if no rep resolved
  // (vacant seat, or context still catching up right after a save). Sending those
  // users back to setup would be a dead end.
  const hasDistrictOnFile =
    userDistrict !== null && userDistrict !== undefined && userDistrict !== "";

  if (!userRepsBioguideID && !addressSkipped && !hasDistrictOnFile) {
    return <Navigate to="/setup-representative" replace />;
  }

  return children;
};

export default ProtectedRoute;
